<template>
  <div class="view" v-if="canView" v-cloak>
      <GridPanelHeader :title="$t('security.activity_logs.title')" :grid-ref="$refs.activityLogsGrid" :show-add="false" :show-filter="false" :show-columns="false" />
      <div class="content-body pt-1">
        <div class="row">
          <div id="toast-msg"></div>
        </div>
        <DxDataGrid
          ref="activityLogsGrid"
          :data-source="dataSource"
          :column-min-width="30"
          style="min-height: 350px"
          :show-column-lines="true"
          :show-row-lines="false"
          :show-borders="true"
          :column-auto-width="true"
          :allow-column-reordering="true"
          :allow-column-resizing="false"
          :row-alternation-enabled="true"
          :remote-operations="gridRemoteOperations"
          :element-attr="gridAttributes"
          @exporting="onExporting"
        >
          <DxColumn
            data-field="created_at"
            :caption="$t('common.created_at')"
            data-type="date"
            :format="getDxGridDefaultDateFormat + ' HH:mm:ss'"
            alignment="center"
            width="12%"
            :allow-sorting="true"
            :filter-operations="['contains']"
            :calculate-filter-expression="calculateCreatedAtFilter"
            :editor-options="{ type: 'date' }"
            :group-index="0"
            sort-order="desc"
            :sort-index="0"
            group-interval="day"
          />
          <DxColumn
            data-field="user.name"
            :caption="$t('common.user')"
            data-type="string"
            width="12%"
            :allow-sorting="false"
            :filter-operations="['contains']"
          />
          <DxColumn
            data-field="action"
            cell-template="activityActionTemplate"
            :caption="$t('security.activity_logs.action')"
            data-type="string"
            width="12%"
            :allow-sorting="false"
            :filter-operations="['=']"
            :calculateCellValue="getActionText"
          >
            <DxLookup :data-source="actionLookup" value-expr="key" display-expr="label" />
          </DxColumn>
          <template #activityActionTemplate="{ data }">
            <span>
              {{ getActionText(data.data.action) }}
            </span>
          </template>
          <DxColumn
            data-field="model_label"
            :caption="$t('security.activity_logs.entity_type')"
            data-type="string"
            width="17%"
            :allow-sorting="false"
            :filter-operations="['contains']"
          />
          <DxColumn
            data-field="model_name"
            :caption="$t('security.activity_logs.entity_identifier')"
            data-type="string"
            width="17%"
            :allow-sorting="false"
            :filter-operations="['contains']"
          />
          <DxColumn
            data-field="changes_readable"
            :caption="$t('common.description')"
            data-type="string"
            :allow-sorting="false"
            :allow-filtering="false"
            :filter-operations="[]"
            :calculate-cell-value="calculateChangesReadable"
            width="30%"
            css-class="changes-col"
          />

          <DxColumn
            :caption="''"
            alignment="center"
            :width="80"
            :min-width="80"
            :max-width="80"
            :fixed="true"
            fixed-position="right"
            :allow-sorting="false"
            :filter-operations="[]"
            css-class="info-col"
            cell-template="infoCellTemplate"
          />

          <template #infoCellTemplate="{ data }">
            <span
              class="dx-icon dx-icon-info info-cell-icon"
              :title="$t('security.activity_logs.view_details')"
              @click="openChangesPopup(data.data)"
            ></span>
          </template>

          <DxPaging :total="3500" :page-size="25" />
          <DxPager
            :visible="true"
            :allowed-page-sizes="pageSizes"
            display-mode="compact"
            :show-page-size-selector="true"
            :show-info="true"
            :show-navigation-buttons="true"
            :info-text="$t('common.page_info')"
          />
          <DxHeaderFilter :visible="false" />
          <DxFilterRow :visible="true" :showOperationChooser="true" apply-filter="auto" />
          <DxSelection mode="single" />
          <DxScrolling column-rendering-mode="virtual" />
          <DxGroupPanel :visible="true" :empty-panel-text="$t('common.group_panel_placeholder')" />
          <DxGrouping :auto-expand-all="true" />
          <DxExport :enabled="true" :allow-export-selected-data="false" :file-name="$t('security.activity_logs.title')" />
          <DxToolbar>
            <DxItem name="groupPanel" />
          </DxToolbar>
        </DxDataGrid>

        <DxPopup
          :visible="popupVisible"
          :width="700"
          :height="420"
          :show-close-button="true"
          :drag-enabled="false"
          :shading="true"
          :hide-on-outside-click="true"
          :title="$t('common.description')"
          @hiding="popupVisible = false"
        >
          <div class="changes-popup">
            <div class="changes-popup-content">{{ changesPopupText }}</div>
            <div class="popup-actions mt-3">
              <Button type="outline-primary" @click="popupVisible = false">{{ $t("common.ok") }}</Button>
            </div>
          </div>
        </DxPopup>
      </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import {
  DxColumn,
  DxDataGrid,
  DxFilterRow,
  DxHeaderFilter,
  DxPager,
  DxPaging,
  DxSelection,
  DxScrolling,
  DxGroupPanel,
  DxGrouping,
  DxExport,
  DxToolbar,
  DxItem,
  DxLookup,
} from "devextreme-vue/data-grid";
import Card from "@/components/Card.vue";
import Button from "@/components/Button.vue";
import CustomStore from "devextreme/data/custom_store";
import axios from "@/plugins/axios";
import * as endpoints from "@/store/api/endpoints";
import { canView, permissions, permissionTypes } from "~/helpers/Permissions";
import DxPopup from "devextreme-vue/popup";
import { exportDataGrid } from "devextreme/excel_exporter";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver-es";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";

export default {
  name: "ActivityLogs",
  middleware: ["auth"],
  components: {
    Card,
    Button,
    DxDataGrid,
    DxColumn,
    DxPaging,
    DxPager,
    DxHeaderFilter,
    DxFilterRow,
    DxSelection,
    DxScrolling,
    DxGroupPanel,
    DxGrouping,
    DxExport,
    DxToolbar,
    DxItem,
    DxPopup,
    DxLookup,
    GridPanelHeader,
  },
  data() {
    return {
      gridAttributes: {
        class: "smagria-grid activity-logs-grid",
      },
      gridRemoteOperations: {
        paging: true,
        filtering: true,
        sorting: true,
        //groupPaging: true, // This tells the grid to send skip/take for groups
        //grouping: true,
      },
      pageSizes: [25, 100, 500, 1000],
      dataSource: new CustomStore({
        key: "uuid",
        load: this.load,
      }),
      popupVisible: false,
      changesPopupText: "",
      actionLookup: [
        { key: "create", label: this.$t("common.create") },
        { key: "update", label: this.$t("common.update") },
        { key: "delete", label: this.$t("common.delete") },
        { key: "list", label: this.$t("security.activity_logs.action_list") },
      ],
    };
  },
  computed: {
    ...mapGetters({
      getDxGridDefaultDateFormat: "settings/getDxGridDefaultDateFormat",
    }),
    config() {
      return {
        permission: permissions.ACCESS_ACTIVITY_LOGS,
        permission_type: permissionTypes.access,
      };
    },
    canView() {
      return canView(this);
    },
  },
  methods: {
    calculateCreatedAtFilter(filterValue, selectedOperation, target) {
      if (!filterValue) return null;
      const toDateOnlyString = (val) => {
        try {
          const d = typeof val === "string" ? new Date(val) : val;
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, "0");
          const day = String(d.getDate()).padStart(2, "0");
          return `${year}-${month}-${day}`;
        } catch (e) {
          return String(val);
        }
      };
      const dateStr = toDateOnlyString(filterValue);
      return ["created_at", "contains", dateStr];
    },
    calculateChangesReadable(rowData) {
      const value = rowData && rowData.changes_readable;
      if (!value) return "";
      if (typeof value === "string") return value;
      if (Array.isArray(value)) {
        return value
          .map((item) => this.stringifyChangeItem(item))
          .filter(Boolean)
          .join("; ");
      }
      try {
        return JSON.stringify(value);
      } catch (e) {
        return String(value);
      }
    },
    stringifyChangeItem(item) {
      if (!item || typeof item !== "object") return "";
      const field_label = item.field_label || item.field || "field";
      const formatVal = (v) => {
        if (v === null || v === undefined || v === "") return "-";
        if (typeof v === "object") {
          try {
            return JSON.stringify(v);
          } catch (e) {
            return String(v);
          }
        }
        return String(v);
      };
      const oldVal = formatVal(item.old);
      const newVal = formatVal(item.new);
      return `${field_label}: ${oldVal} → ${newVal}`;
    },
    getChangesTextForPopup(rowData) {
      const value = rowData && rowData.changes_readable;
      if (!value) return "";
      if (typeof value === "string") return value.replace(/;\s*/g, "\n");
      if (Array.isArray(value)) {
        return value
          .map((item) => this.stringifyChangeItem(item))
          .filter(Boolean)
          .join("\n");
      }
      try {
        return JSON.stringify(value, null, 2);
      } catch (e) {
        return String(value);
      }
    },
    openChangesPopup(rowData) {
      this.changesPopupText = this.getChangesTextForPopup(rowData);
      this.popupVisible = true;
    },
    async load(loadOptions) {
      const dataSource = {
        data: [],
        totalCount: 0,
      };

      try {
        // Use gridInstance.pageSize() to get the constant UI page size.
        let pageLimit = loadOptions.isLoadingAll ? 99999 : loadOptions.take;
        let pageNumber = (loadOptions.skip + loadOptions.take) / pageLimit;

        const payload = {
          page: pageNumber,
          limit: pageLimit,
          filter: loadOptions.filter,
          sort: loadOptions.sort,
        };

        const response = await axios.get(endpoints.ACTIVITY_LOGS, { params: payload });
        const body = response?.data?.data || {};
        dataSource.data = body?.data || [];
        dataSource.totalCount = body?.pagination?.total || (Array.isArray(body?.data) ? body.data.length : 0);
      } catch (e) {
        // Silent fail; grid will show empty state. Errors are handled globally by axios plugin if needed.
        //console.error("Error loading activity logs:", e);
      }

      return dataSource;
    },
    getActionText(actionKey) {
      const actionItem = this.actionLookup.find((item) => item.key === actionKey);
      return actionItem ? actionItem.label : actionKey;
    },
    onExporting(e) {
      const now = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      e.fileName = `Activity_Logs_${now.getFullYear()}_${pad(now.getMonth() + 1)}_${pad(now.getDate())}_${pad(now.getHours())}_${pad(
        now.getMinutes(),
      )}_${pad(now.getSeconds())}`;

      if (e.format === "xlsx") {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("Activity Logs");

        exportDataGrid({
          component: e.component,
          worksheet,
          autoFilterEnabled: true,
          selectedRowsOnly: e.selectedRowsOnly,
          customizeCell: ({ gridCell, excelCell }) => {
            // Normalize empty cells to empty string and clear number formatting
            excelCell.value = excelCell.value == null ? "" : excelCell.value;
            excelCell.numFmt = null;

            // Map action key to localized label in export
            if (gridCell && gridCell.rowType === "data" && gridCell.column && gridCell.column.dataField === "action" && excelCell.row > 1) {
              const actionKey = gridCell.data[gridCell?.column?.dataField];
              excelCell.value = this.getActionText(actionKey);
            }
          },
        }).then(() => {
          workbook.xlsx.writeBuffer().then((buffer) => {
            saveAs(new Blob([buffer], { type: "application/octet-stream" }), e.fileName + ".xlsx");
          });
        });
      }
    },
    onExportingJustFromLocalTable(e) {
      const now = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      e.fileName = `Activity_Logs_${now.getFullYear()}_${pad(now.getMonth() + 1)}_${pad(now.getDate())}_${pad(now.getHours())}_${pad(
        now.getMinutes(),
      )}_${pad(now.getSeconds())}`;

      if (e.format === "xlsx") {
        // Export only the rows currently visible in the grid (no extra backend loads)
        e.cancel = true;
        const grid = e.component;
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("Activity Logs");

        // Visible columns with a dataField (skip command/template-only columns)
        const visibleColumns = grid
          .getVisibleColumns()
          .filter((c) => c && c.dataField && c.visible !== false);

        // Header row
        worksheet.addRow(visibleColumns.map((c) => c.caption || c.dataField));

        // Helper for nested paths like "common.name"
        const getByPath = (obj, path) => {
          if (!obj || !path) return undefined;
          return path.split(".").reduce((acc, key) => (acc != null ? acc[key] : undefined), obj);
        };

        // Data rows from current page/viewport respecting current filters and paging
        const dataRows = grid.getVisibleRows().filter((r) => r && r.rowType === "data");
        dataRows.forEach((r) => {
          const values = visibleColumns.map((col) => {
            if (col.dataField === "action") {
              return this.getActionText(r.data && r.data.action);
            }
            if (col.dataField === "changes_readable") {
              return this.calculateChangesReadable(r.data);
            }
            return getByPath(r.data, col.dataField);
          });
          worksheet.addRow(values);
        });

        workbook.xlsx.writeBuffer().then((buffer) => {
          saveAs(new Blob([buffer], { type: "application/octet-stream" }), e.fileName + ".xlsx");
        });
      }
    },
  },
};
</script>

<style scoped>
.activity-logs-grid {
  width: 100%;
}
/* Allow multiline text only in the description column */
.activity-logs-grid .dx-datagrid-rowsview .dx-row > td.changes-col {
  white-space: normal;
}
.info-cell-icon {
  cursor: pointer;
  font-size: 18px;
  display: inline-block;
  width: 20px;
}

.changes-popup-content {
  white-space: pre-wrap;
  overflow: auto;
  flex: 1 1 auto;
}

.changes-popup {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.popup-actions {
  text-align: right;
}

/* Ensure the popup container itself does not scroll; only content area scrolls */
::v-deep .dx-popup-content {
  overflow: hidden;
}

/* Cap the info column width to 80px (header + rows) */
.activity-logs-grid .dx-datagrid-headers .dx-row > td.info-col,
.activity-logs-grid .dx-datagrid-rowsview .dx-row > td.info-col {
  max-width: 80px;
  width: 80px;
  overflow: hidden;
  white-space: nowrap;
}
</style>
